import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap, map, Observable } from 'rxjs';
import { User } from '../model/User';
import { AppConfigService } from './appConfigService';
import { AuthService } from './auth.service';
import { ClientSearchInput, OneViewClientDetailModel, OneViewClientResponse, OneViewClientResponseV2, OneViewClientSearchOpportunityModel, OpportunityAndServicesForContinuanceEngagementResponseModel, OpportunityServiceByEngagementIdModel } from '../model/Client/OneViewClientDetail';
import { CrmClientSearchRequestModel } from '../model/CRM/CrmClientSearchRequestModel';
import { CrmServiceResponseModel } from '../model/CRM/CrmServiceResponseModel';
import { CrmServiceRequestModel } from '../model/CRM/CrmServiceRequestModel';
import { CrmClientDetailModel, CrmClientDetailResponseModel } from '../model/CRM/CrmClientDetailResponseModel';
import { OneViewClientOpportunityViewModel, OneViewClientOpportunityViewModelWrapper, OpportunityServiceModel, SearchClientOpportunityByRequestModel, SearchOpportunityByEngagementIdRequestModel, SearchOpportunityByOpportunityIdRequestModel, SearchClientOpportunityForSecondaryAssignmentByRequestModel, SearchClientOpportunityForContinuanceByRequestModel } from '../model/Client/CreateClientViewModel';
import { CrmOpportunityModel } from '../model/CRM/CrmOpportunityModel';
import { CrmOpportunitiesForNewEngagementRequestModel } from '../model/CRM/CrmOpportunitiesForNewEngagementRequestModel';
import { CrmOpportunitiesForContinuanceEngagementRequestModel } from '../model/CRM/CrmOpportunitiesForContinuanceEngagementRequestModel';
import { CrmOpportunityByOpportunityIdRequestModel } from '../model/CRM/CrmOpportunityByOpportunityIdRequestModel';
import { CrmOpportunityDetailCrmResponseModel } from '../model/CRM/CrmOpportunityDetailCrmResponseModel';
import { CrmOpportunityByEngagementIdRequestModel } from '../model/CRM/CrmOpportunityByEngagementIdRequestModel';
import { CrmClientDetailByAccountIdRequestModel } from '../model/CRM/CrmClientDetailByAccountIdRequestModel';
import { CrmOpportunityServicesForSecondaryAssignmentRequestModel } from '../model/CRM/CrmOpportunityServicesForSecondaryAssignmentRequestModel';
import { CrmOpportunityListCrmResponseModel } from '../model/CRM/CrmOpportunityListCrmResponseModel';
import { CrmOpportunityAndServicesForContinuanceEngagementRequestModel } from '../model/CRM/CrmOpportunityAndServicesForContinuanceEngagementRequestModel';
import { CrmOpportunityAndServicesForContinuanceEngagementResponseModel } from '../model/CRM/CrmOpportunityAndServicesForContinuanceEngagementResponseModel';
import { CRMResponse } from '../model/Workspace/CRMResponse';
import { WorkspaceService } from 'src/app/services/workspace.service';

@Injectable({
  providedIn: "root"
})
export class CrmService {
  _crmApiEndPoint: string;
  _organizationCode: string;
  _providerName: string;
  APIEndPoint: string;
  userObject: any;
  constructor(
    private environment: AppConfigService,
    private http: HttpClient,
    private authService: AuthService,
    private workspaceService: WorkspaceService
  ) {
    this._crmApiEndPoint = this.environment.config.DATA.API_END_POINT.CRM_INTEGRATION_API_URL;
    this._organizationCode = "US"; //Hardcoded for now. Need to make it dynamic as part of globalization work.
    this._providerName = "OneViewProvider"; //Hardcoded for now. Need to make it dynamic as part of globalization work.
    this.APIEndPoint = this.environment.config.DATA.API_END_POINT.API_URL;
    this.userObject = JSON.parse(localStorage.getItem('currentUser'));
  }

  GetClientsBySearchText(clientSearchInput: ClientSearchInput): Observable<OneViewClientResponseV2<OneViewClientDetailModel[]>> {
    let requestModel = this.GetClientsBySearchText_ConvertToNewRequestModel(clientSearchInput);
    let ApiURL = this._crmApiEndPoint + "/api/clients/search";
    return this.http.post<CrmServiceResponseModel<CrmClientDetailResponseModel>>(ApiURL, requestModel)
      .pipe(
        tap(crmResponse => this.LogCrmTransaction(ApiURL, requestModel, crmResponse)),
        map(crmResponse => this.GetClientsBySearchText_ConvertToOldResponseModel(crmResponse)
        )
      );
  }

  GetClientDetailByAccountId(accountId: string): Observable<OneViewClientResponse<OneViewClientDetailModel>> {
    let requestModel: CrmServiceRequestModel<CrmClientDetailByAccountIdRequestModel> =
    {
      organizationCode: this._organizationCode,
      providerName: this._providerName,
      data: {
        accountId: accountId
      }
    }
    let ApiURL = this._crmApiEndPoint + "/api/clients/search/accountId";
    return this.http.post<CrmServiceResponseModel<CrmClientDetailModel>>(ApiURL, requestModel)
      .pipe(
        tap(crmResponse => this.LogCrmTransaction(ApiURL, requestModel, crmResponse)),
        map(crmResponse => this.GetClientDetailByAccountId_ConvertToOldResponseModel(crmResponse)
        )
      );
  }

  GetOpportunitiesForNewEngagement(searchClientOpportunityByRequestModel: SearchClientOpportunityByRequestModel): Observable<OneViewClientResponseV2<OneViewClientSearchOpportunityModel[]>> {
    let requestModel = this.GetOpportunitiesForNewEngagement_ConvertToNewRequestModel(searchClientOpportunityByRequestModel);
    let ApiURL = this._crmApiEndPoint + "/api/opportunities/search/newEngagement";
    return this.http.post<CrmServiceResponseModel<CrmOpportunityModel[]>>(ApiURL, requestModel)
      .pipe(
        tap(crmResponse => this.LogCrmTransaction(ApiURL, requestModel, crmResponse)),
        map(crmResponse => this.GetOpportunitiesForNewEngagement_ConvertToOldResponseModel(crmResponse)
        )
      );
  }

  GetOpportunityServicesForSecondaryAssignment(searchClientOpportunityForSecondaryAssignmentByRequestModel: SearchClientOpportunityForSecondaryAssignmentByRequestModel): Observable<OneViewClientResponseV2<OneViewClientOpportunityViewModelWrapper>> {
    let requestModel = this.GetOpportunityServicesForSecondaryAssignment_ConvertToNewRequestModel(searchClientOpportunityForSecondaryAssignmentByRequestModel);
    let ApiURL = this._crmApiEndPoint + "/api/opportunities/search/secondaryAssignments";
    return this.http.post<CrmServiceResponseModel<CrmOpportunityListCrmResponseModel>>(ApiURL, requestModel)
      .pipe(
        tap(crmResponse => this.LogCrmTransaction(ApiURL, requestModel, crmResponse)),
        map(crmResponse => this.GetOpportunityServicesForSecondaryAssignment_ConvertToOldResponseModel(crmResponse)
        )
      );
  }

  GetOpportunitiesForContinuanceEngagement(searchClientOpportunityForContinuanceByRequestModel: SearchClientOpportunityForContinuanceByRequestModel): Observable<OneViewClientResponseV2<OpportunityAndServicesForContinuanceEngagementResponseModel[]>> {
    let requestModel = this.GetOpportunitiesForContinuanceEngagement_ConvertToNewRequestModel(searchClientOpportunityForContinuanceByRequestModel);
    let ApiURL = this._crmApiEndPoint + "/api/opportunities/search/continuanceEngagement";
    return this.http.post<CrmServiceResponseModel<CrmOpportunityAndServicesForContinuanceEngagementResponseModel[]>>(ApiURL, requestModel)
      .pipe(
        tap(crmResponse => this.LogCrmTransaction(ApiURL, requestModel, crmResponse)),
        map(crmResponse => this.GetOpportunitiesForContinuanceEngagement_ConvertToOldResponseModel(crmResponse)
        )
      );
  }

  GetOpportunityByOpportunityId(searchOpportunityByOpportunityIdRequestModel: SearchOpportunityByOpportunityIdRequestModel): Observable<OneViewClientResponse<OneViewClientOpportunityViewModelWrapper>> {
    let requestModel = this.GetOpportunityByOpportunityId_ConvertToNewRequestModel(searchOpportunityByOpportunityIdRequestModel);
    let ApiURL = this._crmApiEndPoint + "/api/opportunities/search/opportunityId";
    return this.http.post<CrmServiceResponseModel<CrmOpportunityDetailCrmResponseModel>>(ApiURL, requestModel)
      .pipe(
        tap(crmResponse => this.LogCrmTransaction(ApiURL, requestModel, crmResponse)),
        map(crmResponse => this.GetOpportunityByOpportunityId_ConvertToOldResponseModel(crmResponse)
        )
      );
  }

  GetOpportunityByEngagementId(searchOpportunityByEngagementIdRequestModel: SearchOpportunityByEngagementIdRequestModel): Observable<OneViewClientResponse<OneViewClientOpportunityViewModelWrapper>> {
    let requestModel = this.GetOpportunityByEngagementId_ConvertToNewRequestModel(searchOpportunityByEngagementIdRequestModel);
    let ApiURL = this._crmApiEndPoint + "/api/opportunities/search/engagementId";
    return this.http.post<CrmServiceResponseModel<CrmOpportunityDetailCrmResponseModel>>(ApiURL, requestModel)
      .pipe(
        tap(crmResponse => this.LogCrmTransaction(ApiURL, requestModel, crmResponse)),
        map(crmResponse => this.GetOpportunityByOpportunityId_ConvertToOldResponseModel(crmResponse)
        )
      );
  }

  private GetClientsBySearchText_ConvertToNewRequestModel(clientSearchInput: ClientSearchInput): CrmServiceRequestModel<CrmClientSearchRequestModel> {

    let crmClientSearchRequestModel: CrmClientSearchRequestModel =
    {
      clientSearchText: clientSearchInput.clientSearchText,
      noOfRecords: clientSearchInput.noOfRecords,
      pageNo: clientSearchInput.pageNo
    }

    let requestModel: CrmServiceRequestModel<CrmClientSearchRequestModel> =
    {
      organizationCode: this._organizationCode,
      providerName: this._providerName,
      data: crmClientSearchRequestModel
    }

    return requestModel;
  }

  private GetClientsBySearchText_ConvertToOldResponseModel(crmResponseModel: CrmServiceResponseModel<CrmClientDetailResponseModel>): OneViewClientResponseV2<OneViewClientDetailModel[]> {

    let oneViewClientDetailModel: OneViewClientDetailModel[] = [];

    crmResponseModel?.data?.clientList?.forEach(crmClient => {
      const oldClientModel: OneViewClientDetailModel = {
        hs_segment: crmClient.clientType,
        clientType: this.IsEmptyThenNull(crmClient.clientType),
        name: this.IsEmptyThenNull(crmClient.clientName),
        accountnumber: this.IsEmptyThenNull(crmClient.clientCode),
        gt_cmsaccountname: this.IsEmptyThenNull(crmClient.accountName),
        accountid: this.IsEmptyThenNull(crmClient.accountId),
        hs_accountid: this.IsEmptyThenNull(crmClient.hsAccountId),
        hs_akadba: this.IsEmptyThenNull(crmClient.akaDba),
        gt_industrygroupingid: this.IsEmptyThenNull(crmClient.industry),
        industry: this.IsEmptyThenNull(crmClient.industry),
        hs_siccode_hs_name: this.IsEmptyThenNull(crmClient.sicCode),
        sicCode: this.IsEmptyThenNull(crmClient.sicCode),
        hs_legaltaxstatus: crmClient.structureOrEntity,
        structureOrEntity: this.IsEmptyThenNull(crmClient.structureOrEntity),
        hs_exchange: crmClient.stockExchange,
        stockExchange: this.IsEmptyThenNull(crmClient.stockExchange),
        tickersymbol: this.IsEmptyThenNull(crmClient.tickerSymbol),
        hs_leadsource: crmClient.referralSource,
        referralSource: this.IsEmptyThenNull(crmClient.referralSource),
        hs_fiscalyearend: this.IsEmptyThenNull(crmClient.fiscalYearEndMonth),
        fiscalYearEndMonth: this.IsEmptyThenNull(crmClient.fiscalYearEndMonth),
        telephone1: this.IsEmptyThenNull(crmClient.phoneNumber),
        fax: this.IsEmptyThenNull(crmClient.fax),
        address1_line1: this.IsEmptyThenNull(crmClient.address1),
        address1_line2: this.IsEmptyThenNull(crmClient.address2),
        address1_line3: this.IsEmptyThenNull(crmClient.address3),
        address4: null,
        websiteurl: this.IsEmptyThenNull(crmClient.websiteUrl),
        hs_domainname: this.IsEmptyThenNull(crmClient.domainUrl),
        address1_city: this.IsEmptyThenNull(crmClient.city),
        address1_postalcode: this.IsEmptyThenNull(crmClient.zipCode),
        hs_address1_country: crmClient.country,
        country: this.IsEmptyThenNull(crmClient.country),
        address1_stateorprovince: this.IsEmptyThenNull(crmClient.state),
        cmsClientAdministratorEmail: this.IsEmptyThenNull(crmClient.cmsClientAdministratorEmail),
        cmsClientAdministratorFullname: this.IsEmptyThenNull(crmClient.cmsClientAdministratorFullName),
        customeHeadquarter: null,
        hs_accountsource: null,
        gt_engageclientid: 0,
        hs_siccode: this.IsEmptyThenNull(crmClient.sicCode),
        ownerid: null,
        OppCount: crmClient.totalOpportunityCount,
        hs_clientstatus: this.IsEmptyThenNull(crmClient.clientStatus),
        hs_clientstatus_derivedstatus: null,
        city: this.IsEmptyThenNull(crmClient.city),
        stateorprovince: crmClient.state ?? ""
      }

      oneViewClientDetailModel.push(oldClientModel)
    });

    let responseModel: OneViewClientResponseV2<OneViewClientDetailModel[]> =
    {
      statusCode: crmResponseModel?.isSuccess ? "200" : "500",
      status: crmResponseModel?.message,
      totalRecordCount: crmResponseModel?.data?.totalClientCount ?? 0,
      response: oneViewClientDetailModel
    }
    return responseModel;
  }

  private GetClientDetailByAccountId_ConvertToOldResponseModel(crmResponseModel: CrmServiceResponseModel<CrmClientDetailModel>): OneViewClientResponse<OneViewClientDetailModel> {
    let crmClient = crmResponseModel.data;
    const oldClientModel: OneViewClientDetailModel = {
      hs_segment: crmClient.clientType,
      clientType: crmClient.clientType ?? "",
      name: crmClient.clientName ?? "",
      accountnumber: crmClient.clientCode ?? "",
      gt_cmsaccountname: crmClient.accountName ?? "",
      accountid: crmClient.accountId ?? "",
      hs_accountid: crmClient.hsAccountId ?? "",
      hs_akadba: crmClient.akaDba ?? "",
      gt_industrygroupingid: crmClient.industry ?? "",
      industry: crmClient.industry ?? "",
      hs_siccode_hs_name: crmClient.sicCode ?? "",
      sicCode: crmClient.sicCode ?? "",
      hs_legaltaxstatus: crmClient.structureOrEntity ?? "",
      structureOrEntity: crmClient.structureOrEntity ?? "",
      hs_exchange: crmClient.stockExchange ?? "",
      stockExchange: crmClient.stockExchange ?? "",
      tickersymbol: crmClient.tickerSymbol ?? "",
      hs_leadsource: crmClient.referralSource ?? "",
      referralSource: crmClient.referralSource ?? "",
      hs_fiscalyearend: crmClient.fiscalYearEndMonth ?? "",
      fiscalYearEndMonth: crmClient.fiscalYearEndMonth ?? "",
      telephone1: crmClient.phoneNumber ?? "",
      fax: crmClient.fax ?? "",
      address1_line1: crmClient.address1 ?? "",
      address1_line2: crmClient.address2 ?? "",
      address1_line3: crmClient.address3 ?? "",
      address4: "",
      websiteurl: crmClient.websiteUrl ?? "",
      hs_domainname: crmClient.domainUrl ?? "",
      address1_city: crmClient.city ?? "",
      address1_postalcode: crmClient.zipCode ?? "",
      hs_address1_country: crmClient.country ?? "",
      country: crmClient.country ?? "",
      address1_stateorprovince: crmClient.state ?? "",
      cmsClientAdministratorEmail: crmClient.cmsClientAdministratorEmail ?? "",
      cmsClientAdministratorFullname: crmClient.cmsClientAdministratorFullName ?? "",
      customeHeadquarter: null,
      hs_accountsource: null,
      gt_engageclientid: null,
      hs_siccode: crmClient.sicCode ?? "",
      ownerid: null,
      OppCount: crmClient.totalOpportunityCount,
      hs_clientstatus: crmClient.clientStatus ?? "",
      hs_clientstatus_derivedstatus: null,
      city: crmClient.city ?? "",
      stateorprovince: crmClient.state ?? ""
    }


    let responseModel: OneViewClientResponse<OneViewClientDetailModel> =
    {
      statusCode: crmResponseModel?.isSuccess ? "200" : "500",
      status: crmResponseModel?.message,
      response: oldClientModel
    }
    return responseModel;
  }

  private GetOpportunitiesForNewEngagement_ConvertToNewRequestModel(searchClientOpportunityByRequestModel: SearchClientOpportunityByRequestModel): CrmServiceRequestModel<CrmOpportunitiesForNewEngagementRequestModel> {
    let crmClientSearchRequestModel: CrmOpportunitiesForNewEngagementRequestModel =
    {
      accountId: searchClientOpportunityByRequestModel.AccountId,
      primarySolutionCode: searchClientOpportunityByRequestModel.primarySolutionCode,
      interchangeableSolutionCodesList: searchClientOpportunityByRequestModel.interchangeableSolutionCodesList,
      opportunityId: searchClientOpportunityByRequestModel.opportunityId,
      engagementId: searchClientOpportunityByRequestModel.engagementId
    }

    let requestModel: CrmServiceRequestModel<CrmOpportunitiesForNewEngagementRequestModel> =
    {
      organizationCode: this._organizationCode,
      providerName: this._providerName,
      data: crmClientSearchRequestModel
    }

    return requestModel;
  }

  private GetOpportunitiesForNewEngagement_ConvertToOldResponseModel(crmResponseModel: CrmServiceResponseModel<CrmOpportunityModel[]>): OneViewClientResponseV2<OneViewClientSearchOpportunityModel[]> {
    let oneViewClientSearchOpportunityModel: OneViewClientSearchOpportunityModel[] = [];

    crmResponseModel?.data?.forEach(crmOpportunity => {
      const oldOpportunityModel: OneViewClientSearchOpportunityModel = {
        opportunityId: crmOpportunity.opportunityId,
        opportunityName: crmOpportunity.opportunityName,
        opportunityEstimatedFees: crmOpportunity.opportunityEstimatedFees,
        expectedStartDate: crmOpportunity.expectedStartDate,
        pursuitTeam: crmOpportunity.pursuitTeam,
        officeLocation: crmOpportunity.officeLocation || (crmOpportunity as any).OfficeLocation,
        isOppServicesLinked: crmOpportunity.isOppServicesLinked
      }

      oneViewClientSearchOpportunityModel.push(oldOpportunityModel)
    });

    let responseModel: OneViewClientResponseV2<OneViewClientSearchOpportunityModel[]> =
    {
      statusCode: crmResponseModel?.isSuccess ? "200" : "500",
      status: crmResponseModel?.message,
      totalRecordCount: crmResponseModel?.data?.length ?? 0,
      response: oneViewClientSearchOpportunityModel
    }

    return responseModel;
  }

  private GetOpportunityServicesForSecondaryAssignment_ConvertToNewRequestModel(searchClientOpportunityForSecondaryAssignmentByRequestModel: SearchClientOpportunityForSecondaryAssignmentByRequestModel): CrmServiceRequestModel<CrmOpportunityServicesForSecondaryAssignmentRequestModel> {
    let crmOpportunityServicesForSecondaryAssignmentRequestModel: CrmOpportunityServicesForSecondaryAssignmentRequestModel =
    {
      accountId: searchClientOpportunityForSecondaryAssignmentByRequestModel.AccountId,
      primarySolutionCode: searchClientOpportunityForSecondaryAssignmentByRequestModel.primarySolutionCode,
      secondarySolutionCodesList: searchClientOpportunityForSecondaryAssignmentByRequestModel.secondarySolutionCodesList,
      interchangeableSolutionCodesList: searchClientOpportunityForSecondaryAssignmentByRequestModel.interchangeableSolutionCodesList,
      fetchXml: searchClientOpportunityForSecondaryAssignmentByRequestModel.fetchXML
    }

    let requestModel: CrmServiceRequestModel<CrmOpportunityServicesForSecondaryAssignmentRequestModel> =
    {
      organizationCode: this._organizationCode,
      providerName: this._providerName,
      data: crmOpportunityServicesForSecondaryAssignmentRequestModel
    }

    return requestModel;
  }

  private GetOpportunityServicesForSecondaryAssignment_ConvertToOldResponseModel(crmResponseModel: CrmServiceResponseModel<CrmOpportunityListCrmResponseModel>): OneViewClientResponseV2<OneViewClientOpportunityViewModelWrapper> {
    let oneViewClientOpportunityViewModelWrapper: OneViewClientOpportunityViewModelWrapper =
    {
      isOpportunitySelected: crmResponseModel?.data?.isOpportunitySelected ?? false,
      isOpportunityServicelisted: crmResponseModel?.data?.isOpportunityServicelisted ?? false,
      isOpportunityServiceMatchWithPrimary: crmResponseModel?.data?.isOpportunityServiceMatchWithPrimary ?? false,
      isOpportunityServiceMatchWithPrimaryNSecondary: crmResponseModel?.data?.isOpportunityServiceMatchWithPrimaryNSecondary ?? false,
      interchangeableSolutionCodeFlag: false,
      oneViewClientOpportunityViewModelList: []
    };

    crmResponseModel?.data?.opportunities?.forEach(crmOpportunity => {
      const opportunity = crmOpportunity as any;
      const opportunityLocation = opportunity.OfficeLocation || opportunity.officeLocation || opportunity.GTOfficeLocation ||
        opportunity.gtOfficeLocation || opportunity.gTOfficeLocation || opportunity.gt_locid_gtlocidname;

      let oneViewClientOpportunityViewModel: OneViewClientOpportunityViewModel = {
        opportunityId: crmOpportunity.opportunityId,
        opportunityName: crmOpportunity.opportunityName,
        opportunityEstimatedvalue: crmOpportunity.opportunityEstimatedFees,
        expectedStartDate: crmOpportunity.expectedStartDate,
        estCloseDate: crmOpportunity.estimatedCloseDate,
        keyDicisionMaker: crmOpportunity.keyDicisionMaker,
        pursuitTeam: crmOpportunity.pursuitTeam,
        opportunityServiceList: [],
        OfficeLocation: opportunityLocation,
        gtOfficeLocation: opportunityLocation,
        isAnnuityOpportunity: false,
        accountName: crmOpportunity.accountName,
        pursuitOwner: crmOpportunity.pursuitOwner
      };

      crmOpportunity?.opportunityServices?.forEach(crmOpportunityService => {
        const crmService = crmOpportunityService as any;
        const serviceLocation = crmService.OfficeLocation || crmService.officeLocation || crmService.GTOfficeLocation ||
          crmService.gtOfficeLocation || crmService.gTOfficeLocation || crmService.gt_locid_gtlocidname || opportunityLocation;

        const oldOpportunityServiceModel: OpportunityServiceModel = {
          opportunityServiceId: crmService.opportunityServiceId,
          previousYearOpportunityServiceId: "",
          previousOppServiceEngageAssignmentIds: "",
          solutionCode: crmService.solutionCode,
          solutionDescription: crmService.solutionDescription,
          OfficeLocation: serviceLocation,
          officeLocation: serviceLocation,
          priceAmt: crmService.priceAmount,
          priceAmtFormattedValue: crmService.priceAmountFormattedValue,
          isPrimary: crmService.isPrimary,
          isChecked: crmService.isChecked,
          isAssigned: crmService.isAssigned,
          isContinuanceCarryFrwdAssignment: false,
          isPartOfSecondary: crmService.isPartOfSecondary,
          isEnabled: crmService.isEnabled,
          interchangeableSolutionCodeFlag: crmService.isInterchangeableSolutionCode,
          osaCount: crmService.osaCount,
          opportunityId: crmService.opportunityId,
          opportunityName: crmService.opportunityName,
          opportunityEstimatedvalue: crmService.opportunityEstimatedFees
        }

        oneViewClientOpportunityViewModel.opportunityServiceList.push(oldOpportunityServiceModel)
      });

      oneViewClientOpportunityViewModelWrapper.oneViewClientOpportunityViewModelList.push(oneViewClientOpportunityViewModel);
    });

    let responseModel: OneViewClientResponseV2<OneViewClientOpportunityViewModelWrapper> =
    {
      statusCode: crmResponseModel?.isSuccess ? "200" : "500",
      status: crmResponseModel?.message,
      totalRecordCount: crmResponseModel?.data?.opportunities?.length ?? 0,
      response: oneViewClientOpportunityViewModelWrapper
    }

    return responseModel;
  }

  private GetOpportunitiesForContinuanceEngagement_ConvertToNewRequestModel(searchClientOpportunityForContinuanceByRequestModel: SearchClientOpportunityForContinuanceByRequestModel): CrmServiceRequestModel<CrmOpportunityAndServicesForContinuanceEngagementRequestModel> {
    let crmOpportunityAndServicesForContinuanceEngagementRequestModel: CrmOpportunityAndServicesForContinuanceEngagementRequestModel =
    {
      accountId: searchClientOpportunityForContinuanceByRequestModel.accountId,
      primarySolutionCode: searchClientOpportunityForContinuanceByRequestModel.primarySolutionCode,
      secondarySolutionCodesList: searchClientOpportunityForContinuanceByRequestModel.secondarySolutionCodesList,
      interchangeableSolutionCodesList: searchClientOpportunityForContinuanceByRequestModel.interchangeableSolutionCodesList,
      fetchXml: searchClientOpportunityForContinuanceByRequestModel.fetchXML
    }

    let requestModel: CrmServiceRequestModel<CrmOpportunityAndServicesForContinuanceEngagementRequestModel> =
    {
      organizationCode: this._organizationCode,
      providerName: this._providerName,
      data: crmOpportunityAndServicesForContinuanceEngagementRequestModel
    }

    return requestModel;
  }

  private GetOpportunitiesForContinuanceEngagement_ConvertToOldResponseModel(crmResponseModel: CrmServiceResponseModel<CrmOpportunityAndServicesForContinuanceEngagementResponseModel[]>): OneViewClientResponseV2<OpportunityAndServicesForContinuanceEngagementResponseModel[]> {
    let opportunityAndServicesForContinuanceEngagementResponseModel: OpportunityAndServicesForContinuanceEngagementResponseModel[] = [];

    crmResponseModel?.data?.forEach(crmOpportunity => {
      const oldOpportunityModel: OpportunityAndServicesForContinuanceEngagementResponseModel = {
        opportunityId: crmOpportunity.opportunityId,
        opportunityName: crmOpportunity.opportunityName,
        opportunityEstimatedFees: crmOpportunity.opportunityEstimatedFees,
        expectedStartDate: crmOpportunity.expectedStartDate,
        pursuitTeam: crmOpportunity.pursuitTeam,
        isOppServicesLinked: crmOpportunity.isOppServicesLinked,
        opportunityServiceList: []
      };

      crmOpportunity?.opportunityServiceList?.forEach(crmOpportunityService => {
        const oldOpportunityServiceModel: OpportunityServiceByEngagementIdModel = {
          opportunityServiceId: crmOpportunityService.opportunityServiceId,
          solutionCode: crmOpportunityService.solutionCode,
          solutionDescription: crmOpportunityService.solutionDescription,
          priceAmt: crmOpportunityService.priceAmount,
          priceAmtFormattedValue: crmOpportunityService.priceAmountFormattedValue,
          isPrimary: false,
          isChecked: false,
          isAssigned: false,
          isPartOfSecondary: false,
          isEnabled: false,
          interchangeableSolutionCodeFlag: false,
          osaCount: crmOpportunityService.osaCount
        };

        oldOpportunityModel.opportunityServiceList.push(oldOpportunityServiceModel);
      });

      opportunityAndServicesForContinuanceEngagementResponseModel.push(oldOpportunityModel);
    });

    let responseModel: OneViewClientResponseV2<OpportunityAndServicesForContinuanceEngagementResponseModel[]> =
    {
      statusCode: crmResponseModel?.isSuccess ? "200" : "500",
      status: crmResponseModel?.message,
      totalRecordCount: crmResponseModel?.data?.length ?? 0,
      response: opportunityAndServicesForContinuanceEngagementResponseModel
    }

    return responseModel;
  }

  private GetOpportunityByOpportunityId_ConvertToNewRequestModel(searchOpportunityByOpportunityIdRequestModel: SearchOpportunityByOpportunityIdRequestModel): CrmServiceRequestModel<CrmOpportunityByOpportunityIdRequestModel> {
    let crmOpportunityByOpportunityIdRequestModel: CrmOpportunityByOpportunityIdRequestModel =
    {
      opportunityId: searchOpportunityByOpportunityIdRequestModel.opportunityId,
      engagementId: searchOpportunityByOpportunityIdRequestModel.engagementId,
      primarySolutionCode: searchOpportunityByOpportunityIdRequestModel.primarySolutionCode,
      secondarySolutionCodeList: searchOpportunityByOpportunityIdRequestModel.secSolutionCodeList,
      interchangeableSolutionCodesList: searchOpportunityByOpportunityIdRequestModel.interchangeableSolutionCodesList,
      isPrimaryAssignment: searchOpportunityByOpportunityIdRequestModel.isPrimaryAssignment
    }

    let requestModel: CrmServiceRequestModel<CrmOpportunityByOpportunityIdRequestModel> =
    {
      organizationCode: this._organizationCode,
      providerName: this._providerName,
      data: crmOpportunityByOpportunityIdRequestModel
    }

    return requestModel;
  }

  private GetOpportunityByEngagementId_ConvertToNewRequestModel(searchOpportunityByEngagementIdRequestModel: SearchOpportunityByEngagementIdRequestModel): CrmServiceRequestModel<CrmOpportunityByEngagementIdRequestModel> {
    let crmOpportunityByOpportunityIdRequestModel: CrmOpportunityByEngagementIdRequestModel =
    {
      engagementId: searchOpportunityByEngagementIdRequestModel.engagementId,
      primarySolutionCode: searchOpportunityByEngagementIdRequestModel.primarySolutionCode,
      secondarySolutionCodeList: searchOpportunityByEngagementIdRequestModel.secSolutionCodeList
    }

    let requestModel: CrmServiceRequestModel<CrmOpportunityByEngagementIdRequestModel> =
    {
      organizationCode: this._organizationCode,
      providerName: this._providerName,
      data: crmOpportunityByOpportunityIdRequestModel
    }

    return requestModel;
  }

  private GetOpportunityByOpportunityId_ConvertToOldResponseModel(crmResponseModel: CrmServiceResponseModel<CrmOpportunityDetailCrmResponseModel>): OneViewClientResponse<OneViewClientOpportunityViewModelWrapper> {

    let oneViewClientOpportunityViewModelWrapper: OneViewClientOpportunityViewModelWrapper =
    {
      isOpportunitySelected: crmResponseModel.data.isOpportunitySelected,
      isOpportunityServicelisted: crmResponseModel.data.isOpportunityServicelisted,
      isOpportunityServiceMatchWithPrimary: crmResponseModel.data.isOpportunityServiceMatchWithPrimary,
      isOpportunityServiceMatchWithPrimaryNSecondary: crmResponseModel.data.isOpportunityServiceMatchWithPrimaryNSecondary,
      interchangeableSolutionCodeFlag: false,
      oneViewClientOpportunityViewModelList: []
    };

    const crmOpportunity = crmResponseModel.data.opportunity as any;
    const opportunityLocation = crmOpportunity.OfficeLocation || crmOpportunity.officeLocation || crmOpportunity.GTOfficeLocation ||
      crmOpportunity.gtOfficeLocation || crmOpportunity.gTOfficeLocation || crmOpportunity.gt_locid_gtlocidname;

    let oneViewClientOpportunityViewModel: OneViewClientOpportunityViewModel = {
      opportunityId: crmOpportunity.opportunityId,
      opportunityName: crmOpportunity.opportunityName,
      opportunityEstimatedvalue: crmOpportunity.opportunityEstimatedFees,
      expectedStartDate: crmOpportunity.expectedStartDate,
      estCloseDate: crmOpportunity.estimatedCloseDate,
      keyDicisionMaker: crmOpportunity.keyDicisionMaker,
      pursuitTeam: crmOpportunity.pursuitTeam,
      opportunityServiceList: [],
      OfficeLocation: opportunityLocation,
      gtOfficeLocation: opportunityLocation,
      isAnnuityOpportunity: false,
      accountName: crmOpportunity.accountName,
      pursuitOwner: crmOpportunity.pursuitOwner
    };


    crmResponseModel?.data?.opportunity?.opportunityServices.forEach(crmOpportunityService => {
      const crmService = crmOpportunityService as any;
      const serviceLocation = crmService.OfficeLocation || crmService.officeLocation || crmService.GTOfficeLocation ||
        crmService.gtOfficeLocation || crmService.gTOfficeLocation || crmService.gt_locid_gtlocidname || opportunityLocation;

      const oldOpportunityServiceModel: OpportunityServiceModel = {
        opportunityServiceId: crmService.opportunityServiceId,
        opportunityId: crmOpportunity.opportunityId,
        opportunityName: crmOpportunity.opportunityName,
        opportunityEstimatedvalue: crmOpportunity.opportunityEstimatedFees,
        previousYearOpportunityServiceId: "",
        previousOppServiceEngageAssignmentIds: "",
        solutionCode: crmService.solutionCode,
        solutionDescription: crmService.solutionDescription,
        OfficeLocation: serviceLocation,
        officeLocation: serviceLocation,
        priceAmt: crmService.priceAmount,
        priceAmtFormattedValue: crmService.priceAmountFormattedValue,
        isPrimary: crmService.isPrimary,
        isChecked: crmService.isChecked,
        isAssigned: crmService.isAssigned,
        isContinuanceCarryFrwdAssignment: false,
        isPartOfSecondary: crmService.isPartOfSecondary,
        isEnabled: crmService.isEnabled,
        interchangeableSolutionCodeFlag: crmService.isInterchangeableSolutionCode,
        osaCount: crmService.osaCount
      }

      oneViewClientOpportunityViewModel.opportunityServiceList.push(oldOpportunityServiceModel)
    });

    oneViewClientOpportunityViewModelWrapper.oneViewClientOpportunityViewModelList.push(oneViewClientOpportunityViewModel);


    let responseModel: OneViewClientResponseV2<OneViewClientOpportunityViewModelWrapper> =
    {
      statusCode: crmResponseModel?.isSuccess ? "200" : "500",
      status: crmResponseModel?.message,
      totalRecordCount: crmResponseModel?.data?.opportunity == null ? 0 : 1,
      response: oneViewClientOpportunityViewModelWrapper
    }

    return responseModel;
  }

  IsEmptyThenNull(value: string | null | undefined): string | null {
    return (value === null || value === undefined || value === '') ? null : value;
  }

  private LogCrmTransaction(ApiURL: any,request: any, response: any): void {
    if (this.userObject == null || this.userObject == undefined) {
      this.authService.getUserInfo();
      this.userObject = JSON.parse(localStorage.getItem('currentUser'));
    }
    const logEntry: CRMResponse = {
    api: ApiURL,
    requestBody: JSON.stringify(request),
    responseBody: JSON.stringify(response),
    requestDateTime: response.engageCrmIntegrationDetail?.requestDateTime ?? "",
    requestedBy: this.userObject.loggedInUserEmail,
    responseDateTime: response.engageCrmIntegrationDetail?.responseDateTime ?? "",
    EngagementId: request.data.engagementId ?? 0,
    OpportunityId: request.data.opportunityId ?? "",
    AccountId: request.data.accountId ?? "",
    errorDetail: response.errorDetail?.errorMessage ?? ""
};

    this.workspaceService.storeCRMLog(logEntry).subscribe(statusResult => {
      console.log(statusResult);
    });
 }

}
